import { gql } from '@apollo/client';
import React, { FC, useEffect, useMemo, useState } from 'react';
import {
    BannerFragment,
    CardFragment,
    ContentMatrixFragment,
    PhotosCarouselGalleryFragment,
    PinsFragment,
    SectionAccordion_Entry,
    TipFragment,
} from '../generated/graphql';
import HtmlMapper from 'react-html-map';
import tagMap from '../constants/tagMap';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import AccordionBlock from './blocks/AccordionBlock';
import ButtonBlock from './blocks/ButtonBlock';
import PhotosCarousel from './PhotosCarousel';
import WebcamBlock from './blocks/WebcamBlock';
import VideoBlock from './blocks/VideoBlock';
import MapBlock from './blocks/MapBlock';
import AccommodationsBlock from './blocks/AccommodationsBlock';
import TipBlock from './blocks/TipBlock';
import TilesBlock from './blocks/TilesBlock';
import FormBlock from './blocks/FormBlock';
import BannerBlock from './blocks/BannerBlock';
import EmbedBlock from './blocks/EmbedBlock';
import styled from 'styled-components';
import { Maybe } from '@oberoninternal/travelbase-ds/entities/Maybe';
import CompaniesBlock from './blocks/CompaniesBlock';
import { FAQPage, Question, WithContext } from 'schema-dts';
import striptags from 'striptags';
import SmallContentWrapper from './SmallContentWrapper';
import ActivitiesBlock from './blocks/ActivitiesBlock';
import TravelbaseTilesBlock from './blocks/TravelbaseTilesBlock';
import BoatBlock from './blocks/BoatBlock';
import Head from 'next/head';
import fetchFormData from '../utils/fetchFormData';

export const fragment = gql`
    fragment ContentMatrix on contentMatrix_MatrixField {
        ... on sectionText_Entry {
            text {
                html
            }
        }
        ... on sectionHeader_Entry {
            header
        }
        ... on sectionVideo_Entry {
            videoUrl
        }
        ... on sectionImages_Entry {
            gallery: images {
                ...PhotosCarouselGallery
            }
        }
        ... on sectionAccordion_Entry {
            header
            text {
                html
            }
        }
        ... on sectionButton_Entry {
            button {
                ...LinkField
            }
        }
        ... on sectionWebcam_Entry {
            webcamUrl
        }
        ... on sectionMap_Entry {
            pins {
                ...Pins
            }
        }
        ... on sectionAccommodations_Entry {
            accommodationsQuery
        }
        ... on sectionCompanies_Entry {
            companiesQuery
        }
        ... on sectionActivities_Entry {
            activitiesQuery
        }
        ... on sectionTip_Entry {
            tip {
                ...Tip
            }
        }
        ... on sectionBanner_Entry {
            banner {
                ...Banner
            }
        }
        ... on sectionTiles_Entry {
            ...TilesBlock
        }

        ... on sectionHierarchicalTiles_Entry {
            ...HierarchicalTilesBlock
        }
        ... on sectionEmbed_Entry {
            embed
        }
        ... on sectionForms_Entry {
            id
        }
        ... on sectionTravelbaseTiles_Entry {
            ...TravelbaseTile
        }
        ... on sectionBoat_Entry {
            boatBlockEnabled
        }
    }
    fragment TravelbaseTile on sectionTravelbaseTiles_Entry {
        travelbaseSlugs {
            slug
        }
        travelbaseEntityType
    }
`;

interface Props {
    matrix: ContentMatrixFragment[];
    pageChildren: Maybe<Array<Maybe<CardFragment>>>;
    pageSiblings: Maybe<Array<Maybe<CardFragment>>>;
}

const GROUPED_BLOCKS: ContentMatrixFragment['__typename'][] = [
    'sectionText_Entry',
    'sectionHeader_Entry',
    'sectionAccordion_Entry',
    'sectionBoat_Entry',
];

const ContentMatrix: FC<React.PropsWithChildren<Props>> = ({ matrix, pageChildren, pageSiblings }) => {
    const accordionItems = matrix.filter(
        (item): item is SectionAccordion_Entry => item.__typename === 'sectionAccordion_Entry'
    );

    const questions: Question[] = accordionItems.map(item => ({
        '@type': 'Question',
        name: item.header ?? '',
        acceptedAnswer: {
            '@type': 'Answer',
            text: striptags(item.text?.html ?? ''),
        },
    }));

    const schema: WithContext<FAQPage> = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: questions,
    };

    const groupedMatrixItems = useMemo(() => {
        const toReturn = matrix.reduce<ContentMatrixFragment[][]>(
            (prev, cur) => {
                if (GROUPED_BLOCKS.find(b => b === cur.__typename)) {
                    return [...prev.slice(0, -1), [...prev[prev.length - 1], cur]];
                }
                return [...prev, [cur], []];
            },
            [[]]
        );

        return toReturn[toReturn.length - 1].length === 0 ? toReturn.slice(0, -1) : toReturn;
    }, [matrix]);

    return (
        <Container>
            {accordionItems.length > 0 && (
                <Head>
                    {/* eslint-disable-next-line react/no-danger */}
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
                </Head>
            )}
            {groupedMatrixItems.map((group, gi) =>
                group?.[0]?.__typename && GROUPED_BLOCKS.find(b => b === group[0].__typename) ? (
                    <SmallContentWrapper key={gi}>
                        {group.map((item, i) => (
                            <ContentMatrixItem
                                pageChildren={pageChildren}
                                pageSiblings={pageSiblings}
                                item={item}
                                key={i}
                            />
                        ))}
                    </SmallContentWrapper>
                ) : (
                    group.map((item, i) => (
                        <ContentMatrixItem
                            pageChildren={pageChildren}
                            pageSiblings={pageSiblings}
                            item={item}
                            key={i}
                        />
                    ))
                )
            )}
        </Container>
    );
};

const Container = styled.div``;

const ContentMatrixItem: FC<
    React.PropsWithChildren<{ item: ContentMatrixFragment } & Pick<Props, 'pageChildren' | 'pageSiblings'>>
> = ({ item, pageChildren, pageSiblings }) => {
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        // @ts-ignore
        if (item.__typename === 'sectionForms_Entry' && typeof item.id === 'string') {
            const loadFormData = async () => {
                try {
                    // @ts-ignore
                    const data = await fetchFormData(item.id);
                    setFormData(data.forms);
                } catch (err) {
                    setError(err as Error);
                } finally {
                    setLoading(false);
                }
            };

            loadFormData();
        } else {
            setLoading(false);
        }
    }, [item]);

    if (item.__typename === 'sectionForms_Entry' && loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error loading form data: {error.message}</div>;
    }

    switch (item?.__typename) {
        case 'sectionText_Entry':
            return <HtmlMapper html={item.text?.html ?? ''}>{tagMap}</HtmlMapper>;
        case 'sectionHeader_Entry':
            return <Title variant="regular">{item.header}</Title>;
        case 'sectionVideo_Entry':
            return <VideoBlock videoUrl={item.videoUrl ?? ''} />;
        case 'sectionImages_Entry':
            return (item.gallery?.length ?? 0) > 0 ? (
                <PhotosCarousel gallery={(item.gallery as PhotosCarouselGalleryFragment[]) ?? []} />
            ) : null;
        case 'sectionAccordion_Entry':
            return <AccordionBlock title={item.header ?? ''} text={item.text?.html ?? ''} />;
        case 'sectionButton_Entry':
            return <ButtonBlock link={item.button ?? {}} />;
        case 'sectionWebcam_Entry':
            return <WebcamBlock webcamUrl={item.webcamUrl ?? ''} />;
        case 'sectionMap_Entry':
            return <MapBlock pins={(item.pins as PinsFragment[]) ?? []} />;
        case 'sectionAccommodations_Entry':
            return item.accommodationsQuery ? <AccommodationsBlock queryParams={item.accommodationsQuery} /> : null;
        case 'sectionCompanies_Entry':
            return item.companiesQuery ? <CompaniesBlock queryParams={item.companiesQuery} /> : null;
        case 'sectionActivities_Entry':
            return item.activitiesQuery ? <ActivitiesBlock queryParams={item.activitiesQuery} /> : null;
        case 'sectionTip_Entry':
            return item.tip?.[0] ? <TipBlock tip={item.tip?.[0] as TipFragment} /> : null;
        case 'sectionTravelbaseTiles_Entry':
            return <TravelbaseTilesBlock data={item} />;
        case 'sectionBanner_Entry':
            return item.banner?.[0] ? <BannerBlock banner={item.banner[0] as BannerFragment} /> : null;
        case 'sectionTiles_Entry':
        case 'sectionHierarchicalTiles_Entry':
            return <TilesBlock data={item} pageChildren={pageChildren} pageSiblings={pageSiblings} />;
        case 'sectionEmbed_Entry':
            return item.embed ? <EmbedBlock embed={item.embed} /> : null;
        case 'sectionBoat_Entry':
            return item.boatBlockEnabled ? <BoatBlock /> : null;
        case 'sectionForms_Entry':
            return formData ? <FormBlock forms={formData} /> : null;
        default:
            // eslint-disable-next-line no-console
            console.warn(`ContentMatrixItem: unknown type '${item?.__typename}'`);
            return null;
    }
};

export default ContentMatrix;
